import { DEFAULT_APPEARANCE, type Appearance, type AppearanceDto, type ChatAppearance, type Wallpaper, type WallpaperPreset } from '@dijitalapp/shared';
import { useSyncExternalStore, type CSSProperties } from 'react';
import { api } from './api';
import { activePack, applyThemePack, PACK_CHAT_DIM, PACK_CHAT_VARS, PACK_MINE_VARS, PACK_THEIRS_VARS } from './themePacks';

/*
 * The person's chat look, applied as CSS variables: the accent colour on the whole app, the rest on each chat
 * (with that chat's own wallpaper and bubble colours when set). Loaded once signed in, kept in step with the
 * other devices, and remembered in this browser so the look is there before the first answer arrives.
 */

const CACHE_KEY = 'dijitalapp:appearance';
let state: AppearanceDto = readCache();
const listeners = new Set<() => void>();

function readCache(): AppearanceDto {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw) as AppearanceDto;
  } catch {
    /* private mode */
  }
  return { appearance: DEFAULT_APPEARANCE, chats: {} };
}

function set(next: AppearanceDto) {
  state = { appearance: { ...DEFAULT_APPEARANCE, ...next.appearance }, chats: next.chats };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(state));
  } catch {
    /* private mode */
  }
  applyLook(state.appearance);
  listeners.forEach((l) => l());
}

export async function loadAppearance(): Promise<void> {
  try {
    const saved = await api<AppearanceDto>('GET', '/me/appearance');
    // Changes of mine not saved yet stay on top.
    set({ ...saved, appearance: { ...saved.appearance, ...pending } });
  } catch {
    /* offline: the remembered look stays */
  }
}

// Changes made in quick succession (dragging a slider, several taps) go to the server as one, in order.
let pending: Partial<Appearance> = {};
let timer: ReturnType<typeof setTimeout> | null = null;
let chain: Promise<void> = Promise.resolve();
let waiters: { resolve: () => void; reject: (e: unknown) => void }[] = [];

export function saveAppearance(patch: Partial<Appearance>): Promise<void> {
  // Shown at once; saved a moment later.
  set({ ...state, appearance: { ...state.appearance, ...patch } });
  pending = { ...pending, ...patch };
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, 300);
  return new Promise((resolve, reject) => waiters.push({ resolve, reject }));
}

function flush() {
  timer = null;
  const body = pending;
  const done = waiters;
  pending = {};
  waiters = [];
  chain = chain.then(async () => {
    try {
      const saved = await api<AppearanceDto>('PATCH', '/me/appearance', body);
      // Newer changes still waiting keep showing over this answer.
      set({ ...saved, appearance: { ...saved.appearance, ...pending } });
      done.forEach((w) => w.resolve());
    } catch (err) {
      done.forEach((w) => w.reject(err));
    }
  });
}

export async function saveChatAppearance(conversationId: string, look: ChatAppearance | null): Promise<void> {
  const chats = { ...state.chats };
  if (look && Object.keys(look).length) chats[conversationId] = look;
  else delete chats[conversationId];
  set({ ...state, chats });
  set(await api<AppearanceDto>('PUT', `/conversations/${conversationId}/appearance`, look ?? {}));
}

/** Signed out: back to the defaults (the next person on this browser starts clean). */
export function forgetAppearance(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* private mode */
  }
  set({ appearance: DEFAULT_APPEARANCE, chats: {} });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export function useAppearance(): AppearanceDto {
  return useSyncExternalStore(subscribe, () => state);
}

/** The accent colour and the theme pack (a pack for the whole app brings its own accent). */
function applyLook(look: Appearance) {
  const packOn = applyThemePack(look.themePack);
  applyAccent(packOn && look.themePack?.scope === 'app' ? null : look.accent);
}

function applyAccent(accent: string | null) {
  const root = document.documentElement.style;
  if (accent) {
    root.setProperty('--accent', accent);
    root.setProperty('--accent-soft', `color-mix(in srgb, ${accent} 16%, transparent)`);
  } else {
    root.removeProperty('--accent');
    root.removeProperty('--accent-soft');
  }
}
applyLook(state.appearance);

// ---------- colours ----------

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Readable text on a colour: dark on light, white on dark. */
export const textOn = (hex: string) => (luminance(hex) > 0.42 ? '#111827' : '#ffffff');
/** WCAG contrast ratio of the text colour chosen for this background. */
export function contrast(hex: string): number {
  const a = luminance(hex);
  const b = luminance(textOn(hex));
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

// ---------- wallpapers ----------

// Small repeating drawing for the "doodle" wallpaper (shapes only, drawn in the muted text colour at low opacity).
const DOODLE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Cg fill='none' stroke='%2394a3b8' stroke-opacity='.28' stroke-width='1.6' stroke-linecap='round'%3E%3Cpath d='M14 20c6-8 16-8 22 0M70 14l8 8-8 8M98 24a6 6 0 1 0 0.1 0M20 70h16M28 62v16M64 66c0 8 12 8 12 0s-12-8-12 0M96 64l10 10M106 64l-10 10M18 104l8-8 8 8M62 100h18M98 96c4 6 12 6 16 0'/%3E%3C/g%3E%3C/svg%3E\")";

export const WALLPAPERS: Record<WallpaperPreset, { image: string; size?: string; color?: string }> = {
  dots: { image: 'radial-gradient(color-mix(in srgb, var(--text-muted) 14%, transparent) 1px, transparent 1px)', size: '22px 22px' },
  plain: { image: 'none' },
  doodle: { image: DOODLE, size: '120px 120px' },
  grid: {
    image: 'linear-gradient(color-mix(in srgb, var(--text-muted) 9%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--text-muted) 9%, transparent) 1px, transparent 1px)',
    size: '28px 28px',
  },
  sunset: { image: 'linear-gradient(160deg, #ffe0b8 0%, #ffc1c1 45%, #d9c8ff 100%)' },
  ocean: { image: 'linear-gradient(160deg, #d6f1ff 0%, #b9dcff 50%, #c6f3e9 100%)' },
  forest: { image: 'linear-gradient(160deg, #e3f5d8 0%, #c9ebd0 50%, #b8dfc7 100%)' },
  lavender: { image: 'linear-gradient(160deg, #f1e8ff 0%, #e2d6ff 50%, #fde2f3 100%)' },
  peach: { image: 'linear-gradient(160deg, #fff1e6 0%, #ffe0d1 100%)' },
  night: { image: 'linear-gradient(160deg, #1e293b 0%, #312e81 60%, #4c1d95 100%)', color: '#1e293b' },
};

function wallpaperVars(w: Wallpaper): CSSProperties {
  if (w.kind === 'color') return { '--chat-bg-color': w.color, '--chat-bg-image': 'none' } as CSSProperties;
  const p = WALLPAPERS[w.preset];
  return { '--chat-bg-image': p.image, '--chat-bg-size': p.size ?? 'cover', ...(p.color ? { '--chat-bg-color': p.color } : {}) } as CSSProperties;
}

const RADIUS = { sharp: '4px', normal: '10px', round: '18px' } as const;
const GAP = { compact: '2px', comfortable: '4px', roomy: '9px' } as const;
const FONT = {
  system: 'inherit',
  rounded: "ui-rounded, 'SF Pro Rounded', 'Nunito', 'Varela Round', system-ui, sans-serif",
  serif: "ui-serif, Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
} as const;

/** CSS variables for one chat (or the settings preview): my look, with that chat's own choices on top. */
/** A group's look for everyone, with my own choices for this chat on top (each field separately). */
export function withGroupLook(group: ChatAppearance | null | undefined, mine: ChatAppearance | undefined): ChatAppearance | undefined {
  if (!group) return mine;
  const out: ChatAppearance = { ...group };
  for (const [k, v] of Object.entries(mine ?? {}) as [keyof ChatAppearance, never][]) if (v !== null && v !== undefined) out[k] = v;
  return out;
}

/** A bubble's colour and its readable text, or what stands in for them (a pack's, else the default look). */
function bubbleVars(side: 'mine' | 'theirs', colour: string | null | undefined, otherwise: CSSProperties): CSSProperties {
  return colour ? ({ [`--bubble-${side}-bg`]: colour, [`--bubble-${side}-fg`]: textOn(colour) } as CSSProperties) : otherwise;
}

export function chatStyle(look: Appearance, chat?: ChatAppearance): CSSProperties {
  // A theme pack stands in for my own wallpaper and bubbles (kept for when it is turned off); a chat's own look
  // still comes first.
  const pack = activePack(look);
  const wallpaper = chat?.wallpaper ?? (pack ? null : look.wallpaper);
  const mine = chat?.bubbleMine ?? (pack ? null : look.bubbleMine);
  const theirs = chat?.bubbleTheirs ?? (pack ? null : look.bubbleTheirs);
  return {
    ...(wallpaper ? wallpaperVars(wallpaper) : PACK_CHAT_VARS),
    '--chat-dim': wallpaper ? `${look.wallpaperDim}%` : PACK_CHAT_DIM,
    '--bubble-radius': RADIUS[look.bubbleShape],
    '--chat-text': `${look.textSize}px`,
    '--chat-gap': GAP[look.density],
    '--chat-font': FONT[look.font],
    ...bubbleVars('mine', mine, pack ? PACK_MINE_VARS : {}),
    ...bubbleVars('theirs', theirs, pack ? PACK_THEIRS_VARS : {}),
  } as CSSProperties;
}

/** Up to three emoji and nothing else: shown large when the person likes that. */
export function emojiOnly(text: string | null | undefined): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 40) return false;
  if (!/^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|‍|️|\s)+$/u.test(trimmed)) return false;
  const count = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(trimmed)].filter((s) => s.segment.trim()).length;
  return count > 0 && count <= 3 && !/^[\d#*\s]+$/.test(trimmed);
}
